import LandingNav from '../components/LandingNav';
import LandingFooter from '../components/LandingFooter';
import '../styles/landing.css';

export default function PrivacyPage() {
  return (
    <div className="landing-root">
      <LandingNav solid />
      <div className="landing-section" style={{ paddingTop: 120 }}>
        <div className="landing-section-content" style={{ maxWidth: 760 }}>
          <h1 style={{ fontSize: 40, fontWeight: 800, color: 'var(--color-text-primary)', marginBottom: 8, letterSpacing: '-0.02em' }}>
            Privacy Policy
          </h1>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: 14, marginBottom: 48 }}>
            Effective date: June 26, 2026 &nbsp;·&nbsp; CrewCast Inc.
          </p>

          <Section title="1. Who We Are">
            <p>CrewCast Inc. ("CrewCast", "we", "us", or "our") operates the CrewCast platform — a software-as-a-service product that helps companies collect, review, and manage employee-generated content. This Privacy Policy explains how we collect, use, and protect information when you use our web application at crew-cast.co and our iOS mobile application.</p>
          </Section>

          <Section title="2. Information We Collect">
            <p>We collect the following categories of information:</p>
            <ul>
              <li><strong>Account information</strong> — your name and email address, provided when your employer invites you to CrewCast.</li>
              <li><strong>Content submissions</strong> — photos and videos you upload as part of content briefs ("Moments") created by your employer.</li>
              <li><strong>Usage data</strong> — activity metadata such as last login time, last submission date, and submission history. This is used to power reporting features visible to administrators within your company.</li>
              <li><strong>Device and session data</strong> — on mobile, we store authentication tokens securely in the device keychain. We do not collect device identifiers or location data.</li>
            </ul>
            <p>We do not collect payment information. Reward payouts, if applicable, are managed by your employer outside of the platform.</p>
          </Section>

          <Section title="3. How We Use Your Information">
            <p>We use the information we collect to:</p>
            <ul>
              <li>Authenticate you and maintain your session securely.</li>
              <li>Deliver the core product — displaying your submissions, tracking reward credits, and enabling administrators to review and publish content.</li>
              <li>Show administrators aggregated usage analytics (participation rates, submission counts, last activity) to help them manage their team's content program.</li>
              <li>Send transactional notifications about submissions, approvals, and rewards.</li>
              <li>Investigate and respond to support requests.</li>
            </ul>
            <p>We do not use your information for advertising, and we do not sell or rent your personal data to third parties.</p>
          </Section>

          <Section title="4. How We Store Your Information">
            <p>All data is stored on Amazon Web Services (AWS) infrastructure, including Amazon DynamoDB (database), Amazon S3 (media files), and Amazon Cognito (authentication). AWS services are hosted in the United States.</p>
            <p>Media files (photos and videos) you upload are stored in private S3 buckets accessible only to authenticated users within your company. Access is scoped by company — users cannot access data belonging to other organisations.</p>
            <p>We retain your data for as long as your employer's account is active with CrewCast. Upon account termination, data is deleted within 90 days unless retention is required by applicable law.</p>
          </Section>

          <Section title="5. Data Sharing">
            <p>We do not sell, trade, or share your personal data with third parties for their own purposes. Data is shared only in the following limited circumstances:</p>
            <ul>
              <li><strong>Within your company</strong> — administrators at your employer can view your name, submissions, and activity data as part of operating the content program.</li>
              <li><strong>AWS infrastructure</strong> — data is processed and stored using AWS services as described above. AWS acts as a data processor on our behalf under their standard terms.</li>
              <li><strong>Legal requirements</strong> — we may disclose data if required by law, regulation, or a valid legal process.</li>
            </ul>
          </Section>

          <Section title="6. Your Rights">
            <p>Depending on where you are located, you may have rights regarding your personal data, including the right to access, correct, or request deletion of your information. Because CrewCast is an invite-only business platform, these requests are typically coordinated through your employer (the account administrator).</p>
            <p>To exercise any of these rights, or if you have questions about data held about you, contact us at the address below.</p>
          </Section>

          <Section title="7. Children's Privacy">
            <p>CrewCast is intended for use by employed adults in a professional context. We do not knowingly collect personal information from anyone under the age of 16. If you believe a minor has provided us with personal information, please contact us and we will delete it promptly.</p>
          </Section>

          <Section title="8. Changes to This Policy">
            <p>We may update this Privacy Policy from time to time. When we do, we will revise the effective date at the top of the page. For material changes, we will notify account administrators by email. Continued use of the platform after notice of changes constitutes acceptance of the updated policy.</p>
          </Section>

          <Section title="9. Contact Us">
            <p>If you have questions or concerns about this Privacy Policy or how we handle your data, please contact us:</p>
            <p>
              <strong>CrewCast Inc.</strong><br />
              <a href="/contact" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>crew-cast.co/contact</a>
            </p>
          </Section>
        </div>
      </div>
      <LandingFooter />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 12 }}>
        {title}
      </h2>
      <div style={{ fontSize: 15, color: 'var(--color-text-secondary)', lineHeight: 1.7, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {children}
      </div>
    </div>
  );
}
